<template>
    <div class="relative">
        <canvas
            ref="canvas"
            class="w-full h-full"
        />

        <div
            v-if="isLoading"
            class="bg-white w-full h-full absolute top-0 text-center text-3xl pt-20"
        >
            Lade 3D-Modell...
            <div class="text-3xl text-primary mt-4 animate-spin">
                <br>
                .
            </div>
        </div>

        <div
            v-if="isError"
            class="bg-white w-full h-full absolute top-0 text-center text-3xl pt-20"
        >
            Für diesen Artikel steht leider<br>
            kein 3D-Modell zur Verfügung.
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    onBeforeUnmount, onMounted, ref, watch,
} from 'vue';

const props = defineProps<{
    id: string | number | null
    hasCombinationModel: globalThis.Ref<string | false, string | false>
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const isLoading = ref(false);
const isError = ref(false);

let BABYLON: any = null;
let MATERIALS: any = null;

let engine: any = null;
let scene: any = null;
let arcRotateCamera: any = null;
let ground: any = null;
let hdrTexture: any = null;
let light: any = null;

let groundWidthMultiplier = 0;
let groundHeightMultiplier = 0;

const GROUND_MATERIALS: Record<string, string[]> = {
    grass: ['grass.jpg', 'cwdbimg_208.png', 'cwdbimg_208.jpg', 'cwdbimg_212.png', 'cwdbimg_212.jpg', 'cwdbimg_215.png', 'cwdbimg_215.jpg'],
    sand: ['cwdbimg_231.png', 'cwdbimg_231.jpg'],
    concrete: ['concrete.png'],
    synthetic: ['synthetic.png'],
    synthetic_red: ['synthetic_red.png'],
    pearl_gravel: ['pearl_gravel.png'],
    topsoil: ['topsoil.jpg'],
};

async function ensureBabylonLoaded() {
    if (BABYLON) return;
    // Nuxt 4 / SSR: nur im Client importieren
    BABYLON = await import('babylonjs');
    MATERIALS = await import('babylonjs-materials');
    await import('babylonjs-loaders'); // registriert Loader (gltf/glb etc.)
}

function disposeScene() {
    try {
        if (scene) scene.dispose();
        if (engine) engine.dispose();
    } catch (_) {
    }
    engine = null;
    scene = null;
    arcRotateCamera = null;
    ground = null;
    hdrTexture = null;
    light = null;
}

async function fetchVariantModelName(): Promise<string | null> {
    const {public: pub} = useRuntimeConfig();
    try {
        // Nuxt 2: axios.get(configuratorURL + '/getVariant3DModel/' + id)
        const res = await $fetch<{ model?: string }>(
            `${pub.configuratorURL}/getVariant3DModel/${encodeURIComponent(String(props.id))}`,
            {credentials: 'omit'},
        );
        return res?.model ?? null;
    } catch (e) {
        return null;
    }
}

function createGround() {
    const gridMaterial = new MATERIALS.GridMaterial('groundGrid', scene);
    gridMaterial.gridRatio = 50;
    gridMaterial.majorUnitFrequency = 2;
    gridMaterial.minorUnitVisibility = 0.5;
    gridMaterial.mainColor = new BABYLON.Color3(0, 0, 0);
    gridMaterial.lineColor = BABYLON.Color3.FromHexString('#000');
    gridMaterial.opacity = 0.3;
    gridMaterial.gridOffset = new BABYLON.Vector3(50, 0, 50);

    if (
        (!groundWidthMultiplier || !groundHeightMultiplier)
        || groundWidthMultiplier <= 0
        || groundHeightMultiplier <= 0
    ) {
        groundWidthMultiplier = 50 / 100;
        groundHeightMultiplier = 50 / 100;
    }

    if (ground) ground.dispose();
    ground = BABYLON.Mesh.CreateGround(
        'gridGround',
        10000 * groundWidthMultiplier,
        10000 * groundHeightMultiplier,
        2,
        scene,
    );
    ground.isPickable = false;
    ground.position.y = 0.1;
    ground.material = gridMaterial;
}

function isMeshGround(mesh: any): boolean {
    if (!mesh) return false;
    const mat = mesh.material;
    const texUrl = mat?.albedoTexture?.url;
    if (!texUrl) return false;
    const texture = texUrl.substring(texUrl.lastIndexOf('/') + 1);
    for (const type in GROUND_MATERIALS) {
        if (GROUND_MATERIALS[type].includes(texture)) return true;
    }
    return false;
}

function updateCamera(modelNode: any) {
    const boundingInfo = modelNode.getHierarchyBoundingVectors(true);
    const center = new BABYLON.Vector3(
        (boundingInfo.max.x + boundingInfo.min.x) / 2,
        (boundingInfo.max.y + boundingInfo.min.y) / 2,
        (boundingInfo.max.z + boundingInfo.min.z) / 2,
    );
    const dX = boundingInfo.max.x - boundingInfo.min.x;
    const dY = boundingInfo.max.y - boundingInfo.min.y;
    const dZ = boundingInfo.max.z - boundingInfo.min.z;
    const radius = Math.max(dX, dY, dZ) / 2;

    const camera = arcRotateCamera;
    camera.setTarget(center);
    camera.minZ = 10;

    const aspectRatio = engine.getAspectRatio(camera);
    let halfMinFov = camera.fov / 2;
    if (aspectRatio < 1) halfMinFov = Math.atan(aspectRatio * Math.tan(camera.fov / 2));

    let viewRadius = Math.abs(radius / Math.sin(halfMinFov));
    viewRadius *= 1.2;

    if (camera.upperRadiusLimit != null && viewRadius > camera.upperRadiusLimit) {
        camera.upperRadiusLimit = viewRadius;
    }
    camera.radius = viewRadius;
}

function loadModel(model: string) {
    const {public: pub} = useRuntimeConfig();
    const path = props.hasCombinationModel.value
        ? `/uploads/files/ar/combinations/${props.hasCombinationModel.value}/`
        : '/uploads/models/';

    BABYLON.SceneLoader.ImportMesh(
        null,
        `${pub.configuratorURL}${path}`,
        model,
        scene,
        (meshes: any[]) => {
            try {
                const modelScaler = new BABYLON.TransformNode('scale', scene);
                let groundMesh: any = null;

                for (const mesh of meshes) {
                    if (isMeshGround(mesh)) groundMesh = mesh;
                    mesh.isPickable = true;
                    mesh.setParent(modelScaler);
                }

                if (model.endsWith('.gltf') || model.endsWith('.glb')) {
                    const scale = 100; // gltf: m, babylon: cm
                    modelScaler.scaling.x = scale;
                    modelScaler.scaling.y = scale;
                    modelScaler.scaling.z = scale;
                }

                if (groundMesh) {
                    const {max} = groundMesh.getHierarchyBoundingVectors(true);
                    modelScaler.position.y = -max.y;
                } else {
                    const {
                        min,
                        max,
                    } = modelScaler.getHierarchyBoundingVectors(true);
                    modelScaler.position.x = -(min.x + max.x) / 2;
                    modelScaler.position.z = -(min.z + max.z) / 2;
                    modelScaler.position.y = -min.y;
                }

                updateCamera(modelScaler);
            } catch (_) {
                // still ok
            }
            isLoading.value = false;
        },
        null,
        () => {
            isLoading.value = false;
            isError.value = true;
        },
    );
}

function createScene(model: string) {
    if (!canvas.value) return;

    disposeScene();

    engine = new BABYLON.Engine(canvas.value);
    scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(1, 1, 1, 1);

    arcRotateCamera = new BABYLON.ArcRotateCamera(
        'Camera',
        Math.PI / 3,
        Math.PI / 3,
        500,
        new BABYLON.Vector3(0, 50, 0),
        scene,
    );
    arcRotateCamera.panningSensibility = Infinity;
    arcRotateCamera.wheelPrecision = 0.4;
    arcRotateCamera.upperBetaLimit = Math.PI / 2;
    arcRotateCamera.attachControl(canvas.value, false);
    arcRotateCamera.layerMask = 1;
    arcRotateCamera.minZ = 100;
    arcRotateCamera.maxZ = 17500;
    arcRotateCamera.upperRadiusLimit = 500;
    arcRotateCamera.lowerRadiusLimit = 200;
    arcRotateCamera.useAutoRotationBehavior = true;
    arcRotateCamera.useNaturalPinchZoom = true;

    hdrTexture = new BABYLON.CubeTexture('/assets/viewer/textures/studio_cube_specular.env', scene);
    hdrTexture.gammaSpace = false;
    scene.environmentTexture = hdrTexture;

    light = new BABYLON.DirectionalLight('*dir00', new BABYLON.Vector3(0.3, -0.6, 0.3), scene);
    light.position = new BABYLON.Vector3(-30, 500, 60);

    createGround();
    loadModel(model);

    engine.runRenderLoop(() => {
        scene?.render();
    });

    const onResize = () => engine?.resize();
    window.addEventListener('resize', onResize);
    // sauber entfernen
    onBeforeUnmount(() => window.removeEventListener('resize', onResize));
}

async function boot() {
    if (import.meta.server) return;
    if (isLoading.value) return;

    isError.value = false;
    isLoading.value = true;

    await ensureBabylonLoaded();

    if (props.hasCombinationModel) {
        createScene('scene_ar.gltf');
        return;
    }

    const model = await fetchVariantModelName();
    if (!model) {
        isLoading.value = false;
        isError.value = true;
        return;
    }

    createScene(model);
}

onMounted(() => {
    boot();
});

// Wenn Variant/ID wechselt: neu laden
watch(
    () => [props.id, props.hasCombinationModel],
    () => boot(),
);

onBeforeUnmount(() => {
    disposeScene();
});
</script>
